import moment from 'moment';
import { ConversationAction } from '../actions/conversationActions';
import { initialRootState } from '../context';
import { AppConversation } from '../models/conversation';

// sort conversations by lastUpdateOn date (most recently updated first), fallback to startedOn if lastUpdateOn is not available
const sortConversations = (a: AppConversation, b: AppConversation) => {
    const aDate = a.lastUpdateOn ?? a.startedOn;
    const bDate = b.lastUpdateOn ?? b.startedOn;
    return moment(aDate).isBefore(bDate) ? 1 : -1;
};

export const conversationsReducer = (chats: AppConversation[], action: ConversationAction): AppConversation[] => {
    switch (action.type) {
        case 'SET_CONVERSATIONS': {
            // if no conversations are available, return the initial state
            if (!action.payload.length) {
                return initialRootState.chats;
            }
            const items = action.payload.sort(sortConversations);
            return items;
        }
        case 'ADD_CONVERSATION':
            // Don't add draft conversations to the main list, but still process them for active conversation management
            if (action.payload.draft && action.payload.id === 'DRAFT') {
                // For draft conversations, update the existing draft or add it temporarily
                const existingDraftIndex = chats.findIndex(c => c.id === 'DRAFT');
                if (existingDraftIndex >= 0) {
                    // Replace existing draft
                    return chats.map((conversation, index) => 
                        index === existingDraftIndex ? { ...action.payload, active: true } : { ...conversation, active: false }
                    );
                } else {
                    // Add draft temporarily for navigation purposes
                    return [...chats, { ...action.payload, active: true }].map((conversation) => {
                        return { ...conversation, active: conversation.id === action.payload.id };
                    });
                }
            }
            return [...chats, { ...action.payload, draft: false, active: true }].map((conversation) => {
                // conversation ID is set during the conversation creation process, on the front-end
                return { ...conversation, active: conversation.id === action.payload.id };
            });
        case 'APPEND_CONVERSATIONS': {
            // Append new conversations, avoiding duplicates
            // Sort the combined list to maintain chronological order
            const existingIds = new Set(chats.map(c => c.id));
            const newConversations = action.payload
                .filter(c => !existingIds.has(c.id))
                .map(c => ({ ...c, draft: false, active: false }));
            return [...chats, ...newConversations].sort(sortConversations);
        }
        case 'REMOVE_CONVERSATION': {
            const newItems = chats.filter((conversation) => conversation.id !== action.payload);
            if (!newItems.length) {
                return initialRootState.chats;
            }
            return newItems;
        }
        case 'CLEAR_CONVERSATIONS':
            return initialRootState.chats;
        case 'UPDATE_CONVERSATION': {
            // Update conversation
            const oldConversation = chats.find(c => c.id === action.payload.id);
            const updated = chats.map((conversation) => {
                return conversation.id === action.payload.id ? { ...conversation, ...action.payload.patch } : conversation;
            });
            
            // Only re-sort if lastUpdateOn actually changed
            const newConversation = updated.find(c => c.id === action.payload.id);
            const oldDate = oldConversation?.lastUpdateOn;
            const newDate = newConversation?.lastUpdateOn;
            
            if (oldDate && newDate && moment(oldDate).isSame(newDate)) {
                // lastUpdateOn didn't change, don't re-sort
                return updated;
            }
            
            // lastUpdateOn changed, re-sort to move updated conversation to the top
            return updated.sort(sortConversations);
        }
        case 'UPDATE_ACTIVE_CONVERSATION': {
            // Update conversation
            const oldConversation = chats.find(c => c.id === action.payload.id);
            const updated = chats.map((conversation) => {
                return conversation.id === action.payload.id
                    ? { ...conversation, draft: false, ...action.payload.conversation, prompts: action.payload.conversation.prompts?.map((p) => ({ ...p, aborted: p.aborted || p.answer === null })) }
                    : conversation;
            });
            
            // Only re-sort if lastUpdateOn actually changed
            const newConversation = updated.find(c => c.id === action.payload.id);
            const oldDate = oldConversation?.lastUpdateOn;
            const newDate = newConversation?.lastUpdateOn;
            
            if (oldDate && newDate && moment(oldDate).isSame(newDate)) {
                // lastUpdateOn didn't change, don't re-sort
                return updated;
            }
            
            // lastUpdateOn changed, re-sort to move updated conversation to the top
            return updated.sort(sortConversations);
        }

        default:
            return chats;
    }
};
